import { useQuery } from '@tanstack/react-query';
import { apiClient } from './client';
import type {
  AuditLog,
  ErrorLog,
  LogsStats,
  QueryAuditLogsParams,
  QueryErrorLogsParams,
  PaginatedResponse,
} from '../types/adminLogs';

// Query keys
export const adminLogsKeys = {
  all: ['adminLogs'] as const,
  auditLogs: () => [...adminLogsKeys.all, 'audit'] as const,
  auditList: (filters: QueryAuditLogsParams) => [...adminLogsKeys.auditLogs(), 'list', filters] as const,
  auditDetail: (id: string) => [...adminLogsKeys.auditLogs(), 'detail', id] as const,
  errorLogs: () => [...adminLogsKeys.all, 'errors'] as const,
  errorList: (filters: QueryErrorLogsParams) => [...adminLogsKeys.errorLogs(), 'list', filters] as const,
  errorDetail: (id: string) => [...adminLogsKeys.errorLogs(), 'detail', id] as const,
  stats: () => [...adminLogsKeys.all, 'stats'] as const,
  entityTypes: () => [...adminLogsKeys.all, 'entityTypes'] as const,
};

// Helper to build query params
const buildQueryParams = <T extends object>(params: T): string => {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  }
  return searchParams.toString();
};

// API functions
const fetchAuditLogs = async (query: QueryAuditLogsParams): Promise<PaginatedResponse<AuditLog>> => {
  const params = buildQueryParams(query);
  const response = await apiClient.get(`/admin/logs/audit?${params}`);
  return response.data;
};

const fetchAuditLog = async (id: string): Promise<{ success: boolean; data: AuditLog }> => {
  const response = await apiClient.get(`/admin/logs/audit/${id}`);
  return response.data;
};

const fetchErrorLogs = async (query: QueryErrorLogsParams): Promise<PaginatedResponse<ErrorLog>> => {
  const params = buildQueryParams(query);
  const response = await apiClient.get(`/admin/logs/errors?${params}`);
  return response.data;
};

const fetchErrorLog = async (id: string): Promise<{ success: boolean; data: ErrorLog }> => {
  const response = await apiClient.get(`/admin/logs/errors/${id}`);
  return response.data;
};

const fetchLogsStats = async (): Promise<{ success: boolean; data: LogsStats }> => {
  const response = await apiClient.get('/admin/logs/stats');
  return response.data;
};

const fetchEntityTypes = async (): Promise<{ success: boolean; data: string[] }> => {
  const response = await apiClient.get('/admin/logs/entity-types');
  return response.data;
};

// React Query hooks
export function useAuditLogs(query: QueryAuditLogsParams = {}) {
  return useQuery({
    queryKey: adminLogsKeys.auditList(query),
    queryFn: () => fetchAuditLogs(query),
  });
}

export function useAuditLog(id: string) {
  return useQuery({
    queryKey: adminLogsKeys.auditDetail(id),
    queryFn: () => fetchAuditLog(id),
    enabled: !!id,
  });
}

export function useErrorLogs(query: QueryErrorLogsParams = {}) {
  return useQuery({
    queryKey: adminLogsKeys.errorList(query),
    queryFn: () => fetchErrorLogs(query),
  });
}

export function useErrorLog(id: string) {
  return useQuery({
    queryKey: adminLogsKeys.errorDetail(id),
    queryFn: () => fetchErrorLog(id),
    enabled: !!id,
  });
}

export function useLogsStats() {
  return useQuery({
    queryKey: adminLogsKeys.stats(),
    queryFn: fetchLogsStats,
  });
}

export function useEntityTypes() {
  return useQuery({
    queryKey: adminLogsKeys.entityTypes(),
    queryFn: fetchEntityTypes,
  });
}
